import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, Link, useParams } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { handleUpdate, handleGet } from "../../../controllers/controller.all";
import { toast } from "react-toastify";
import SubmitButton from "../../../components/ui/SubmitButton";


export default function PlanningUpdate() {
    const { id } = useParams();
    const user = useSelector((state) => state.userReducer?.user);
    const token = user?.access_token || "";
    const ecoleId = user?.ecole?._id || user?.ecole;
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [classes, setClasses] = useState([]);
    const [matieres, setMatieres] = useState([]);
    const [salles, setSalles] = useState([]);
    const [allPlannings, setAllPlannings] = useState([]);

    const [formData, setFormData] = useState({
        classe: "",
        matiere: "",
        salle: "",
        heure_debut: "",
        heure_fin: "",
        jour: "",
        date_fin: ""
    });
    const [isRecurring, setIsRecurring] = useState(false);

    useEffect(() => {
        const fetchRefs = async () => {
            const promises = [
                handleGet(`/classe/ecole/${ecoleId}`, (clsRes) => {
                    if (clsRes?.classes) setClasses(clsRes.classes.filter(c => c.ecole === ecoleId || c.ecole?._id === ecoleId));
                }, token),
                handleGet(`/matiere/ecole/${ecoleId}`, (matRes) => {
                    if (matRes?.matieres) setMatieres(matRes.matieres.filter(c => c.ecole === ecoleId || c.ecole?._id === ecoleId));
                }, token),
                handleGet(`/salle/ecole/${ecoleId}`, (salleRes) => {
                    if (salleRes?.salles) setSalles(salleRes.salles.filter(c => c.ecole === ecoleId || c.ecole?._id === ecoleId));
                }, token),
                handleGet(`/planning/ecole/${ecoleId}`, (planRes) => {
                    if (planRes?.plannings) setAllPlannings(planRes.plannings);
                }, token)
            ];

            if (id) {
                promises.push(
                    handleGet(`/planning/${id}`, (res) => {
                        if (res?.planning) {
                            const p = res.planning;
                            const j = p.jour || "";
                            setIsRecurring(!/^\d{4}-\d{2}-\d{2}$/.test(j) && j !== "");
                            setFormData({
                                classe: p.classe?._id || p.classe || "",
                                matiere: p.matiere?._id || p.matiere || "",
                                salle: p.salle?._id || p.salle || "",
                                heure_debut: p.heure_debut || "",
                                heure_fin: p.heure_fin || "",
                                jour: j,
                                date_fin: p.date_fin || ""
                            });
                        }
                    }, token)
                );
            }
            await Promise.all(promises);
        };
        if (token) fetchRefs();
    }, [token, id, ecoleId]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        if (formData.heure_debut >= formData.heure_fin) {
            e.preventDefault();
            toast.warn("L'heure de début doit être avant l'heure de fin.");
            return;
        }

        const isConflict = allPlannings.some(p => {
            if (p._id === id) return false; // ignore self
            const sameClasse = (p.classe?._id === formData.classe || p.classe === formData.classe);
            const sameJour = (p.jour === formData.jour);
            if (!sameClasse || !sameJour) return false;

            return (formData.heure_debut < p.heure_fin && formData.heure_fin > p.heure_debut);
        });

        if (isConflict) {
            e.preventDefault();
            toast.error("Conflit d'horaire pour cette classe à cette date.");
            return;
        }

        const payload = {
            ...formData,
            ecole: ecoleId
        };
        const res = await handleUpdate(e, `/planning/${id}`, payload, setLoading, token);
        if (res) {
            navigate("/plannings");
        }
    };

    return (
        <div className="flex flex-col gap-6 max-w-2xl mx-auto">
            <div className="flex items-center gap-4">
                <Link to="/plannings" className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition text-gray-500">
                    <ArrowLeft className="h-5 w-5" />
                </Link>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Modifier le Planning</h1>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="bg-white shadow-sm border border-gray-200 rounded-xl overflow-hidden">
                <div className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Classe *</label>
                            <select required name="classe" value={formData.classe} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white">
                                <option value="">Sélectionner</option>
                                {classes.map(c => <option key={c._id} value={c._id}>{c.libelle}</option>)}
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Matière *</label>
                            <select required name="matiere" value={formData.matiere} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white">
                                <option value="">Sélectionner</option>
                                {matieres.map(m => <option key={m._id} value={m._id}>{m.libelle}</option>)}
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Salle *</label>
                            <select required name="salle" value={formData.salle} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white">
                                <option value="">Sélectionner</option>
                                {salles.map(s => <option key={s._id} value={s._id}>{s.libelle}</option>)}
                            </select>
                        </div>
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <label className="text-sm font-medium text-gray-700">Date / Jour *</label>
                                <div className="flex items-center gap-2">
                                    <input 
                                        type="checkbox" 
                                        id="isRecurringUpdate"
                                        checked={isRecurring}
                                        onChange={(e) => {
                                            setIsRecurring(e.target.checked);
                                            setFormData({ ...formData, jour: "" }); // Reset jour when switching
                                        }}
                                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                    />
                                    <label htmlFor="isRecurringUpdate" className="text-xs text-gray-600 cursor-pointer">Cours récurrent</label>
                                </div>
                            </div>
                            
                            {isRecurring ? (
                                <div className="space-y-3">
                                    <select 
                                        required 
                                        name="jour" 
                                        value={formData.jour} 
                                        onChange={handleChange} 
                                        className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                                    >
                                        <option value="">Sélectionner un jour</option>
                                        <option value="lundi">Lundi</option>
                                        <option value="mardi">Mardi</option>
                                        <option value="mercredi">Mercredi</option>
                                        <option value="jeudi">Jeudi</option>
                                        <option value="vendredi">Vendredi</option>
                                        <option value="samedi">Samedi</option>
                                        <option value="dimanche">Dimanche</option>
                                    </select>
                                    <div className="space-y-1 mt-2">
                                        <label className="text-sm font-medium text-gray-700">Date de fin de récurrence (Optionnel)</label>
                                        <input 
                                            type="date" 
                                            name="date_fin" 
                                            value={formData.date_fin} 
                                            onChange={handleChange} 
                                            className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" 
                                        />
                                        <p className="text-xs text-gray-500">Laissez vide si ce cours n'a pas de fin prévue.</p>
                                    </div>
                                </div>
                            ) : (
                                <input 
                                    required 
                                    type="date" 
                                    name="jour" 
                                    value={formData.jour} 
                                    onChange={handleChange} 
                                    className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" 
                                />
                            )}
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Heure de début *</label>
                            <input required type="time" name="heure_debut" value={formData.heure_debut} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Heure de fin *</label>
                            <input required type="time" name="heure_fin" value={formData.heure_fin} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                        </div>
                    </div>
                </div>
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                    <Link to="/plannings" className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition">
                        Annuler
                    </Link>
                    <SubmitButton type="submit" isSubmitting={loading} disabled={loading} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition disabled:opacity-50">
                        <Save className="h-4 w-4" />
                        Mettre à jour
                    </SubmitButton>
                </div>
            </form>
        </div>
    );
}
